import { useForm } from "@inertiajs/react";
import {
  Children,
  cloneElement,
  type FormEvent,
  type InputHTMLAttributes,
  isValidElement,
  type ReactElement,
  type ReactNode,
  type TextareaHTMLAttributes,
} from "react";

import Button from "@/components/Button";
import LinkButton from "@/components/LinkButton";
import SearchableSelect from "@/components/SearchableSelect";
import { usePermissions } from "@/lib/permissions";
import { courses } from "@/routes";
import type { Course } from "@/types";

const USER_CATEGORY_LABELS: Record<string, string> = {
  senora: "Señora",
  universitaria: "Universitaria",
  numeraria_auxiliar: "Numeraria Auxiliar",
  agregada: "Agregada",
  numeraria: "Numeraria",
  la_cuesta: "S V",
  la_cuesta_sn: "S SV",
  la_cuesta_cp: "CP SV",
};

const CHECKBOX_FIELDS = [
  { name: "published", label: "Publicado" },
  { name: "is_public", label: "Público" },
  { name: "enable_waitlist", label: "Habilitar lista de espera" },
] as const;

const FIELD_INPUT_CLASSES =
  "w-full border-0 bg-transparent p-0 font-sans text-base font-light text-ink shadow-none placeholder:text-ink/40 focus:outline-none focus:ring-0 focus:shadow-none";

function Section({
  icon,
  title,
  children,
}: {
  icon: ReactNode;
  title: string;
  children: ReactNode;
}) {
  return (
    <fieldset className="flex flex-col gap-4 rounded-2xl border-2 border-ink/20 p-5">
      <legend className="flex items-center gap-2 px-2 font-sans text-sm font-semibold text-ink">
        <span className="text-steel">{icon}</span>
        {title}
      </legend>
      {children}
    </fieldset>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  const child = Children.only(children);
  const childProps = isValidElement(child)
    ? (child.props as { value?: unknown; required?: boolean })
    : undefined;
  const hasValue =
    childProps?.value !== undefined &&
    childProps?.value !== null &&
    childProps?.value !== "";
  const invalid = !!childProps?.required && !hasValue;
  const displayLabel = childProps?.required ? `${label} *` : label;
  const showLegend = hasValue || invalid;

  const content = isValidElement(child)
    ? cloneElement(child as ReactElement<{ placeholder?: string }>, {
        placeholder:
          (child.props as { placeholder?: string }).placeholder ?? displayLabel,
      })
    : child;

  return (
    <fieldset
      className={`min-w-0 rounded-full border-2 px-4 focus-within:border-steel ${showLegend ? "pb-2.5" : "py-3.5"} ${invalid ? "border-deep-red" : "border-ink"}`}
    >
      {showLegend && (
        <legend
          className={`truncate px-1.5 font-sans text-xs ${invalid ? "text-deep-red" : "text-ink/60"}`}
        >
          {displayLabel}
        </legend>
      )}
      {content}
    </fieldset>
  );
}

function Input(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={FIELD_INPUT_CLASSES} {...props} />;
}

function Textarea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={`${FIELD_INPUT_CLASSES} resize-y`} {...props} />;
}

function toTimeInput(time: string | null): string {
  if (!time) return "";
  const match = time.match(/T(\d{2}:\d{2})/);
  return match ? match[1] : time.slice(0, 5);
}

type Props = {
  data: Course;
  categories: { id: number; name: string }[];
  houses: { id: number; name: string }[];
  centers: { id: number; name: string }[];
  directors: { id: number; name: string; rut: string }[];
  assignable_users: { id: number; name: string; rut: string }[];
};

export default function CoursesEdit({
  data: course,
  categories,
  houses,
  centers,
  directors,
  assignable_users: assignableUsers,
}: Props) {
  const { isAdmin } = usePermissions();
  const isNew = !course.id;
  const form = useForm({
    name: course.name ?? "",
    identifier: course.identifier ?? "",
    description: course.description ?? "",
    start_date: course.start_date ?? "",
    end_date: course.end_date ?? "",
    start_time: toTimeInput(course.start_time),
    end_time: toTimeInput(course.end_time),
    amount: course.amount != null ? String(course.amount) : "",
    max_quota: course.max_quota != null ? String(course.max_quota) : "",
    payment_window: String(course.payment_window ?? ""),
    user_category: course.user_category ?? "",
    quantity_of_donations: String(course.quantity_of_donations ?? ""),
    final_amount:
      course.final_amount != null ? String(course.final_amount) : "",
    final_inscriptions:
      course.final_inscriptions != null
        ? String(course.final_inscriptions)
        : "",
    category_id: course.category_id ? String(course.category_id) : "",
    house_id: course.house_id ? String(course.house_id) : "",
    center_id: course.center_id ? String(course.center_id) : "",
    user_id: course.user_id ? String(course.user_id) : "",
    responsible_user_id: course.responsible_user_id
      ? String(course.responsible_user_id)
      : "",
    published: course.published ?? false,
    is_public: course.is_public ?? false,
    enable_waitlist: course.enable_waitlist ?? false,
  });

  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!e.currentTarget.checkValidity()) {
      e.currentTarget.reportValidity();
      return;
    }
    form.transform((data) => ({
      course: {
        ...data,
        category_id: data.category_id || null,
        house_id: data.house_id || null,
        center_id: data.center_id || null,
        user_id: data.user_id || null,
        responsible_user_id: data.responsible_user_id || null,
        final_amount: data.final_amount || null,
        final_inscriptions: data.final_inscriptions || null,
      },
    }));
    if (isNew) {
      form.post(courses.create().url);
    } else {
      form.patch(courses.update(course.id).url);
    }
  }

  return (
    <div className="mx-auto w-[90%] max-w-2xl py-10">
      <h1 className="mb-1 font-sans text-3xl font-semibold text-ink">
        {isNew ? "Nuevo curso" : course.name || "Curso"}
      </h1>
      <p className="mb-6 font-sans text-sm text-ink/60">
        Los campos marcados con * son obligatorios.
      </p>

      <form onSubmit={submit} noValidate className="flex flex-col gap-4">
        <Section icon={<InfoIcon />} title="Datos generales">
          <div className="grid grid-cols-1 gap-4">
            {!isNew && (
              <Field label="Código">
                <Input
                  value={form.data.identifier}
                  onChange={(e) => form.setData("identifier", e.target.value)}
                />
              </Field>
            )}
            <Field label="Tipo de Curso">
              <SearchableSelect
                value={form.data.category_id}
                onChange={(v) => form.setData("category_id", v)}
                options={[
                  { value: "", label: "Sin Tipo de Curso" },
                  ...categories.map((c) => ({
                    value: String(c.id),
                    label: c.name,
                  })),
                ]}
                required
              />
            </Field>
            <Field label="Nombre">
              <Input
                value={form.data.name}
                onChange={(e) => form.setData("name", e.target.value)}
                required
              />
            </Field>
            <Field label="Descripción">
              <Textarea
                rows={3}
                value={form.data.description}
                onChange={(e) => form.setData("description", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        <Section icon={<CalendarIcon />} title="Fecha y hora">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Fecha de inicio">
              <Input
                type="date"
                value={form.data.start_date}
                onChange={(e) => form.setData("start_date", e.target.value)}
                required
              />
            </Field>
            <Field label="Hora de inicio">
              <Input
                type="time"
                value={form.data.start_time}
                onChange={(e) => form.setData("start_time", e.target.value)}
                required
              />
            </Field>
            <Field label="Fecha de fin">
              <Input
                type="date"
                value={form.data.end_date}
                onChange={(e) => form.setData("end_date", e.target.value)}
                required
              />
            </Field>
            <Field label="Hora de fin">
              <Input
                type="time"
                value={form.data.end_time}
                onChange={(e) => form.setData("end_time", e.target.value)}
                required
              />
            </Field>
          </div>
        </Section>

        <Section icon={<MoneyIcon />} title="Cupos y aportes">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Aporte">
              <Input
                type="number"
                min={0}
                value={form.data.amount}
                onChange={(e) => form.setData("amount", e.target.value)}
              />
            </Field>
            <Field label="Cupos máximos">
              <Input
                type="number"
                min={0}
                value={form.data.max_quota}
                onChange={(e) => form.setData("max_quota", e.target.value)}
                required
              />
            </Field>
            <Field label="Días de plazo de pago">
              <Input
                type="number"
                min={0}
                value={form.data.payment_window}
                onChange={(e) => form.setData("payment_window", e.target.value)}
              />
            </Field>
            <Field label="Aporte final (opcional)">
              <Input
                type="number"
                min={0}
                value={form.data.final_amount}
                onChange={(e) => form.setData("final_amount", e.target.value)}
              />
            </Field>
            <Field label="Inscripciones finales (opcional)">
              <Input
                type="number"
                min={0}
                value={form.data.final_inscriptions}
                onChange={(e) =>
                  form.setData("final_inscriptions", e.target.value)
                }
              />
            </Field>
          </div>
        </Section>

        <Section icon={<BuildingIcon />} title="Organización">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Casa">
              <SearchableSelect
                value={form.data.house_id}
                onChange={(v) => form.setData("house_id", v)}
                options={[
                  { value: "", label: "Sin casa" },
                  ...houses.map((h) => ({
                    value: String(h.id),
                    label: h.name,
                  })),
                ]}
                required
              />
            </Field>
            <Field label="Centro a cargo">
              <SearchableSelect
                value={form.data.center_id}
                onChange={(v) => form.setData("center_id", v)}
                options={[
                  { value: "", label: "Sin centro" },
                  ...centers.map((c) => ({
                    value: String(c.id),
                    label: c.name,
                  })),
                ]}
                required
              />
            </Field>
            <Field label="Cuenta Institucional Encargada/o">
              <SearchableSelect
                value={form.data.user_id}
                onChange={(v) => form.setData("user_id", v)}
                options={[
                  { value: "", label: "Sin Asignar Institucional" },
                  ...directors.map((d) => ({
                    value: String(d.id),
                    label: `${d.name} (${d.rut})`,
                  })),
                ]}
              />
            </Field>
            <Field label="Curso(s) para">
              <SearchableSelect
                value={form.data.user_category}
                onChange={(v) => form.setData("user_category", v)}
                options={[
                  { value: "", label: "Sin categoría de usuario" },
                  ...Object.entries(USER_CATEGORY_LABELS).map(
                    ([value, label]) => ({ value, label }),
                  ),
                ]}
              />
            </Field>
            {isAdmin && (
              <Field label="Encargada/o de actividad (opcional)">
                <SearchableSelect
                  value={form.data.responsible_user_id}
                  onChange={(v) => form.setData("responsible_user_id", v)}
                  options={[
                    { value: "", label: "Sin Asignar Encargada/o" },
                    ...assignableUsers.map((u) => ({
                      value: String(u.id),
                      label: `${u.name} (${u.rut})`,
                    })),
                  ]}
                />
              </Field>
            )}
          </div>
        </Section>

        <Section icon={<ToggleIcon />} title="Visibilidad">
          <div className="flex flex-col gap-2">
            {CHECKBOX_FIELDS.map(({ name, label }) => (
              <label
                key={name}
                className="flex w-fit cursor-pointer items-center gap-2 self-start font-sans text-sm text-ink"
              >
                <input
                  type="checkbox"
                  checked={form.data[name]}
                  onChange={(e) => form.setData(name, e.target.checked)}
                  className="h-4 w-4 rounded-full border-ink/50 text-steel focus:ring-steel/40"
                />
                <span>{label}</span>
              </label>
            ))}
          </div>
        </Section>

        <div className="mt-2 flex gap-3">
          <Button type="submit" disabled={form.processing}>
            Guardar
          </Button>
          <LinkButton
            href={isNew ? courses.index().url : courses.show(course.id).url}
          >
            Cancelar
          </LinkButton>
        </div>
      </form>
    </div>
  );
}

function InfoIcon() {
  return (
    <svg
      className="h-5 w-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <path strokeLinecap="round" d="M12 11v5.5M12 8v.01" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg
      className="h-5 w-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden="true"
    >
      <rect x="3.5" y="4.5" width="17" height="16" rx="2" />
      <path strokeLinecap="round" d="M3.5 9h17M8 3v3M16 3v3" />
    </svg>
  );
}

function MoneyIcon() {
  return (
    <svg
      className="h-5 w-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <path
        strokeLinecap="round"
        d="M9.5 15.5c0 1 .9 1.5 2.5 1.5s2.5-.6 2.5-1.6-1-1.4-2.5-1.7-2.5-.7-2.5-1.7S10.4 10.5 12 10.5s2.5.5 2.5 1.5M12 9v1.2M12 16.8V18"
      />
    </svg>
  );
}

function BuildingIcon() {
  return (
    <svg
      className="h-5 w-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden="true"
    >
      <rect x="4" y="3" width="12" height="18" rx="1" />
      <path strokeLinecap="round" d="M8 7h4M8 11h4M8 15h4M16 10h4v11h-4" />
    </svg>
  );
}

function ToggleIcon() {
  return (
    <svg
      className="h-5 w-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden="true"
    >
      <rect x="3" y="8" width="18" height="8" rx="4" />
      <circle cx="8" cy="12" r="2.5" />
    </svg>
  );
}
